import React from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  Typography, 
  Accordion, 
  AccordionSummary, 
  AccordionDetails, 
  Chip,
  Box,
  Grid,
  Paper
} from '@mui/material';
import { Visibility } from '@mui/icons-material';
import JSONView from '@uiw/react-json-view';
import AgentNode from '../../models/Nodes/AgentNode';

interface AgentViewModalProps {
  open: boolean;
  onClose: () => void;
  agent: AgentNode | null;
}

const AgentViewModal: React.FC<AgentViewModalProps> = ({ 
  open, 
  onClose, 
  agent
}) => {
  if (!agent) return null;

  const steps = agent.proceduralMemory?.steps || [];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <Visibility />
          <Typography variant="h6">Agent Details</Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2, spaceY: 3 }}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="subtitle2" color="textSecondary">ID:</Typography>
                <Typography variant="body2">{agent.id}</Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="subtitle2" color="textSecondary">Type:</Typography>
                <Typography variant="body2">{agent.type}</Typography>
              </Box>
              {agent.parentAgentId && (
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="subtitle2" color="textSecondary">Parent Agent:</Typography>
                  <Chip label={agent.parentAgentId} color="primary" size="small" />
                </Box>
              )}
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="subtitle2" color="textSecondary">Status:</Typography>
                <Chip 
                  label={agent.isEnabled ? 'Enabled' : 'Disabled'} 
                  color={agent.isEnabled ? 'success' : 'error'} 
                  size="small" 
                />
              </Box>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Typography variant="subtitle2" color="textSecondary">Required Parameters:</Typography>
                <Box>
                  {agent.requiredParameters && agent.requiredParameters.length > 0 ? (
                    <Box display="flex" flexWrap="wrap" gap={1}>
                      {agent.requiredParameters.map((param) => (
                        <Chip key={param} label={param} color="secondary" size="small" />
                      ))}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="textSecondary">No required parameters</Typography>
                  )}
                </Box>
              </Box>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="subtitle2" color="textSecondary">Model:</Typography>
                <Typography variant="body2">{agent.proceduralMemory?.model || 'No model'}</Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="subtitle2" color="textSecondary">Temperature:</Typography>
                <Typography variant="body2">{agent.proceduralMemory?.modelTemperature || 0}</Typography>
              </Box>
            </Box>
          </Paper>

          <Box>
            <Typography variant="h6" sx={{ mb: 2 }}>Steps ({steps.length})</Typography>
            {steps.map((step, index) => (
              <Accordion key={step.id}>
                <AccordionSummary>
                  <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
                    <Typography>{step.description || `Step ${index + 1}`}</Typography>
                    <Chip label={step.type} color="primary" size="small" />
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <JSONView value={step.definition} />
                </AccordionDetails>
              </Accordion>
            ))}
            {steps.length === 0 && (
              <Box textAlign="center" py={2}>
                <Typography color="textSecondary">No steps defined</Typography>
              </Box>
            )}
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default AgentViewModal;